import { createElement } from 'react';
import { matchPath } from 'react-router-dom';

import { company } from '~/company/Company';
import type { CompanyName } from '~/company/interface';
import { preloadModal } from '~/components/ModalList/modalMap';
import { ROUTES } from '~/pages/Root/constants';

import {
  type LazyRoute,
  lazyRoute,
  type RouteLoader,
  type RoutePick,
} from './lazyRoute';

/**
 * Route modules that get their own JS/CSS chunk.
 *
 * The glob keys are also the module ids Vite writes into the SSR manifest, so
 * a loader and the manifest entry the server looks its stylesheet up by can
 * never drift apart. A path that does not exist simply has no key, which
 * `loaderFor` turns into a startup error instead of a silent missing stylesheet.
 */
const ROUTE_MODULES = import.meta.glob([
  '/src/pages/Catalog/components/CatalogV3/CatalogV3.tsx',
  '/src/pages/ShopV3/ShopV3.tsx',
  '/src/pages/ShopV3Saas/ShopV3Saas.tsx',
  '/src/pages/Product/Product.tsx',
  '/src/pages/Favs/Favs.tsx',
  '/src/pages/CartV3/CartV3.tsx',
  '/src/pages/Checkout/Checkout.tsx',
  '/src/pages/cabinetRoutes.ts',
  '/src/pages/Order/Order.tsx',
  '/src/pages/OrderV3/OrderV3.tsx',
  '/src/pages/OrderPayment/OrderPayment.tsx',
  '/src/pages/LoyaltyProgram/LoyaltyProgram.tsx',
  '/src/pages/SearchResultsV3/SearchResultsV3.tsx',
  '/src/pages/Legals/Legals.tsx',
  '/src/pages/CD/Brand/index.tsx',
  '/src/pages/CD/Careers/index.tsx',
  '/src/pages/CD/FAQ/index.tsx',
  '/src/pages/CD/Influencers/index.tsx',
  '/src/pages/CD/OurStory/index.tsx',
  '/src/pages/CD/Refund/index.tsx',
  '/src/pages/CD/ShippingReturn/index.tsx',
  '/src/pages/Cerise/ContactUs/ContactUs.tsx',
  '/src/pages/ContactsPage/ContactsPage.tsx',
  '/src/pages/DubaiExpress/AboutPage/AboutPage.tsx',
  '/src/pages/DubaiExpress/DeliveryPage/DeliveryPage.tsx',
  '/src/pages/DubaiExpress/FAQPage/FAQPage.tsx',
  '/src/pages/DubaiExpress/ReturnsPage/ReturnsPage.tsx',
  '/src/pages/DubaiExpress/SMIPage/SMIPage.tsx',
  '/src/pages/Electronium/About/About.tsx',
  '/src/pages/Electronium/CustomerCare/CustomerCare.tsx',
  '/src/pages/Electronium/FAQ/FAQ.tsx',
  '/src/pages/Electronium/Payment/Payment.tsx',
  '/src/pages/Localee/AboutPage/AboutPage.tsx',
]);

interface SplitRoute {
  /** Absolute path pattern, matched with react-router's `matchPath`. */
  path: string;
  /** Module id as it appears in Vite's SSR manifest. */
  moduleId: string;
  route: LazyRoute;
  /** Whether this entry serves the company of the request being handled. */
  serves?: () => boolean;
}

const SPLIT_ROUTES: SplitRoute[] = [];

function loaderFor(moduleId: string): RouteLoader {
  const loader = ROUTE_MODULES[`/${moduleId}`];
  if (!loader) {
    throw new Error(
      `routes/manifest: "${moduleId}" is missing from the route module glob`,
    );
  }
  return loader as RouteLoader;
}

interface DefineOptions {
  pick?: RoutePick;
  /**
   * Brands this route belongs to. Several brands map different pages onto the
   * same path (`/faq`), so registering all of them would preload — and inject
   * the stylesheet of — the wrong page.
   */
  brands?: CompanyName[];
  /**
   * Whether this entry serves the current company, for variants that a brand
   * list cannot express (`company.isSaas`).
   */
  serves?: () => boolean;
}

/**
 * Registers a route module as its own chunk and returns the component to render.
 *
 * Nothing here may read `company`. This module is evaluated once, and on the
 * server that happens at startup — long before `configureCompanyConfig` sets
 * the company for a request — so any decision taken now would freeze to the
 * default brand and be served to every other one. Company-dependent choices
 * belong in `serves`, which runs per request, or in the render of the exported
 * component.
 */
function define(
  moduleId: string,
  paths: string[],
  options: DefineOptions = {},
): LazyRoute {
  const route = lazyRoute(moduleId, loaderFor(moduleId), options.pick);
  const { brands, serves } = options;
  const servesCompany =
    serves ?? (brands ? () => brands.includes(company.name) : undefined);

  for (const path of paths) {
    SPLIT_ROUTES.push({ path, moduleId, route, serves: servesCompany });
  }

  return route;
}

/**
 * Defers the choice between per-company variants of a route to render time —
 * on the server that is inside the request, which is the only moment the
 * company is known.
 */
function variantRoute(choose: () => LazyRoute) {
  const Variant = () => createElement(choose());
  Variant.displayName = 'VariantRoute';
  return Variant;
}

const cabinet = (child: string) => `${ROUTES.cabinet}/${child}`;
const pickNamed =
  (name: string): RoutePick =>
  (module) =>
    module[name] as never;

export const CatalogRoute = define(
  'src/pages/Catalog/components/CatalogV3/CatalogV3.tsx',
  [ROUTES.catalog],
);
const ShopSaas = define('src/pages/ShopV3Saas/ShopV3Saas.tsx', [ROUTES.shop], {
  serves: () => company.isSaas,
});
const Shop = define('src/pages/ShopV3/ShopV3.tsx', [ROUTES.shop], {
  serves: () => !company.isSaas,
});
export const ShopRoute = variantRoute(() => (company.isSaas ? ShopSaas : Shop));
export const ProductRoute = define('src/pages/Product/Product.tsx', [
  ROUTES.product,
]);
export const FavsRoute = define('src/pages/Favs/Favs.tsx', [ROUTES.favs]);
export const CartRoute = define('src/pages/CartV3/CartV3.tsx', [ROUTES.cart]);
export const CheckoutRoute = define('src/pages/Checkout/Checkout.tsx', [
  ROUTES.checkout,
]);
const OrderV3 = define('src/pages/OrderV3/OrderV3.tsx', [ROUTES.order], {
  serves: () => company.is('mcosmetic'),
});
const Order = define('src/pages/Order/Order.tsx', [ROUTES.order], {
  serves: () => !company.is('mcosmetic'),
});
export const OrderRoute = variantRoute(() =>
  company.is('mcosmetic') ? OrderV3 : Order,
);
export const OrderPaymentRoute = define(
  'src/pages/OrderPayment/OrderPayment.tsx',
  [ROUTES.order_payment],
  { pick: pickNamed('OrderPaymentPage') },
);
export const LoyaltyRoute = define(
  'src/pages/LoyaltyProgram/LoyaltyProgram.tsx',
  [`/${ROUTES.loyalty}`],
);
const CABINET = 'src/pages/cabinetRoutes.ts';
export const CabinetRoute = define(CABINET, [ROUTES.cabinet], {
  pick: pickNamed('Cabinet'),
});
const ORDERS_PATHS = [
  cabinet(ROUTES.cabinet_orders),
  cabinet(`${ROUTES.cabinet_orders}/${ROUTES.cabinet_orders_tab}`),
];
// Both variants live in the cabinet module, so registering each separately
// costs no extra chunk — only the named export differs.
const OrdersV3 = define(CABINET, ORDERS_PATHS, {
  pick: pickNamed('OrdersV3'),
  serves: () => company.is('mcosmetic'),
});
const Orders = define(CABINET, ORDERS_PATHS, {
  pick: pickNamed('Orders'),
  serves: () => !company.is('mcosmetic'),
});
export const OrdersRoute = variantRoute(() =>
  company.is('mcosmetic') ? OrdersV3 : Orders,
);
export const PersonalDataRoute = define(
  CABINET,
  [cabinet(ROUTES.cabinet_personalData)],
  { pick: pickNamed('PersonalData') },
);
export const AddressRoute = define(CABINET, [cabinet(ROUTES.cabinet_address)], {
  pick: pickNamed('Address'),
});
export const EventsRoute = define(CABINET, [cabinet(ROUTES.cabinet_events)], {
  pick: pickNamed('Events'),
});
export const SubscriptionRoute = define(
  CABINET,
  [cabinet(ROUTES.cabinet_subscriptions)],
  { pick: pickNamed('Subscription') },
);
export const PaymentMethodRoute = define(
  CABINET,
  [cabinet(ROUTES.cabinet_paymentMethod)],
  { pick: pickNamed('PaymentPage') },
);
export const ReferralRoute = define(
  CABINET,
  [cabinet(ROUTES.cabinet_referral)],
  { pick: pickNamed('ReferralProgram') },
);
export const PromocodesRoute = define(
  CABINET,
  [
    cabinet(ROUTES.cabinet_promocodes),
    cabinet(`${ROUTES.cabinet_promocodes}/${ROUTES.cabinet_promocodes_tab}`),
  ],
  { pick: pickNamed('Promocodes') },
);
export const SearchRoute = define(
  'src/pages/SearchResultsV3/SearchResultsV3.tsx',
  [ROUTES.search],
);
export const LegalsRoute = define('src/pages/Legals/Legals.tsx', [
  ROUTES.legals,
  `${ROUTES.legals}/:legalSlug`,
]);

const CITYDRINKS = ['citydrinks'] as CompanyName[];
const DUBAI_EXPRESS = ['dubaiexpress', 'dubaiexpresscom'] as CompanyName[];
const ELECTRONIUM = ['electronium'] as CompanyName[];
const CERISE = ['cerise'] as CompanyName[];
const LOCALEE = ['localee'] as CompanyName[];

export const BrandRoute = define(
  'src/pages/CD/Brand/index.tsx',
  [ROUTES.brand],
  { brands: CITYDRINKS },
);
export const CareersRoute = define(
  'src/pages/CD/Careers/index.tsx',
  [ROUTES.careers],
  { brands: CITYDRINKS },
);
export const FaqRoute = define('src/pages/CD/FAQ/index.tsx', [ROUTES.faq], {
  brands: CITYDRINKS,
});
export const InfluencersRoute = define(
  'src/pages/CD/Influencers/index.tsx',
  [ROUTES.influencers],
  { brands: CITYDRINKS },
);
export const OurStoryRoute = define(
  'src/pages/CD/OurStory/index.tsx',
  [ROUTES.ourStory],
  { brands: CITYDRINKS },
);
export const RefundRoute = define(
  'src/pages/CD/Refund/index.tsx',
  [ROUTES.refund],
  { brands: CITYDRINKS },
);
export const ShippingReturnRoute = define(
  'src/pages/CD/ShippingReturn/index.tsx',
  [ROUTES.shippingReturn],
  { brands: CITYDRINKS },
);
export const ContactUsCeriseRoute = define(
  'src/pages/Cerise/ContactUs/ContactUs.tsx',
  [ROUTES.contactUs],
  { brands: CERISE },
);
export const AboutDeRoute = define(
  'src/pages/DubaiExpress/AboutPage/AboutPage.tsx',
  [ROUTES.about],
  { brands: DUBAI_EXPRESS },
);
export const ContactsRoute = define(
  'src/pages/ContactsPage/ContactsPage.tsx',
  [ROUTES.contacts],
  { brands: [...DUBAI_EXPRESS, 'mcosmetic' as CompanyName] },
);
export const DeliveryDeRoute = define(
  'src/pages/DubaiExpress/DeliveryPage/DeliveryPage.tsx',
  [ROUTES.delivery],
  { brands: DUBAI_EXPRESS },
);
export const FaqDeRoute = define(
  'src/pages/DubaiExpress/FAQPage/FAQPage.tsx',
  [ROUTES.faq],
  { brands: DUBAI_EXPRESS },
);
export const ReturnsDeRoute = define(
  'src/pages/DubaiExpress/ReturnsPage/ReturnsPage.tsx',
  [ROUTES.returns],
  { brands: DUBAI_EXPRESS },
);
export const SmiDeRoute = define(
  'src/pages/DubaiExpress/SMIPage/SMIPage.tsx',
  [ROUTES.smi],
  { brands: DUBAI_EXPRESS },
);
export const AboutLocaleeRoute = define(
  'src/pages/Localee/AboutPage/AboutPage.tsx',
  [ROUTES.about],
  { brands: LOCALEE },
);
export const AboutElectroniumRoute = define(
  'src/pages/Electronium/About/About.tsx',
  [ROUTES.about],
  { brands: ELECTRONIUM, pick: pickNamed('About') },
);
export const CustomerCareRoute = define(
  'src/pages/Electronium/CustomerCare/CustomerCare.tsx',
  [ROUTES.customerCare],
  { brands: ELECTRONIUM, pick: pickNamed('CustomerCare') },
);
export const FaqElectroniumRoute = define(
  'src/pages/Electronium/FAQ/FAQ.tsx',
  [ROUTES.faq],
  { brands: ELECTRONIUM, pick: pickNamed('FAQ') },
);
export const PaymentElectroniumRoute = define(
  'src/pages/Electronium/Payment/Payment.tsx',
  [ROUTES.payment],
  { brands: ELECTRONIUM, pick: pickNamed('Payment') },
);

/**
 * Loads the chunks needed to render `url` and reports the module ids that
 * matched, so the caller can look their assets up in the SSR manifest.
 *
 * `url` carries the query string because a `?modal=` deep link renders a modal
 * on top of the page, and its chunk has to be resolved before the first render
 * too.
 */
export async function preloadRoutesFor(url: string): Promise<string[]> {
  const [pathname = '/', search] = url.split('?');
  const matched = SPLIT_ROUTES.filter(
    (entry) =>
      matchPath({ path: entry.path, end: true }, pathname) &&
      (entry.serves?.() ?? true),
  );

  const [, modalModuleIds] = await Promise.all([
    Promise.all(matched.map((entry) => entry.route.preload())),
    preloadModal(new URLSearchParams(search).get('modal')),
  ]);

  return [
    ...new Set([...matched.map((entry) => entry.moduleId), ...modalModuleIds]),
  ];
}
